import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';

const BeardProcess = () => {
  const data = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  
  // Only render if treatmentProcess exists (has images) and NOT on eyebrow page
  if (!data.treatmentProcess || data.treatmentProcess.length === 0 || data.features?.analysisType === 'eyebrow') {
    return null;
  }

  const { treatmentProcess, variant } = data;
  
  // Dynamic titles based on variant and language
  const isBeard = variant === 'bart';
  
  const sectionBadge = language === 'de' ? 'Der Behandlungsprozess' : 'The Treatment Process';
  const sectionTitle = language === 'de' ? 'Behandlungsprozess' : 'Treatment Process';
  const sectionSubtitle = isBeard 
    ? (language === 'de' 
        ? 'Transparente Darstellung aller Schritte von der ersten Planung bis zum endgültigen Ergebnis Ihrer Barthaartransplantation'
        : 'Transparent presentation of all steps from initial planning to the final result of your beard hair transplant')
    : (language === 'de'
        ? 'Transparente Darstellung aller Schritte von der ersten Planung bis zum endgültigen Ergebnis Ihrer Haartransplantation'
        : 'Transparent presentation of all steps from initial planning to the final result of your hair transplant');

  // Process step translations
  const processTranslations = {
    'Planung': { en: 'Planning' },
    'Entnahme': { en: 'Extraction' },
    'Einpflanzung': { en: 'Implantation' },
    'Endergebnis': { en: 'Final Result' },
  };

  const descriptionTranslations = {
    'In der Planungsphase wird die Bartkontur sorgfältig entworfen und die Anzahl der benötigten Grafts bestimmt. Eine detaillierte Analyse Ihrer Spenderzonen ermöglicht eine präzise und individuelle Planung.': {
      en: 'In the planning phase, the beard contour is carefully designed and the number of grafts needed is determined. A detailed analysis of your donor zones enables precise and individual planning.'
    },
    'Bei der Entnahme werden einzelne Haarfollikel aus der Spenderzone (meist am Hinterkopf) entnommen. Diese Follikeleinheiten werden sorgfältig unter dem Mikroskop vorbereitet.': {
      en: 'During extraction, individual hair follicles are removed from the donor zone (usually the back of the head). These follicular units are carefully prepared under the microscope.'
    },
    'Die vorbereiteten Haarfollikel werden präzise in die Empfängerzonen im Bartbereich eingesetzt. Dabei wird besonders auf den natürlichen Wuchswinkel geachtet.': {
      en: 'The prepared hair follicles are precisely placed into the recipient zones in the beard area. Special attention is paid to the natural growth angle.'
    },
    'Nach 12-18 Monaten ist das endgültige Ergebnis sichtbar. Die transplantierten Barthaare wachsen natürlich und können wie Ihr eigener Bart gestylt werden.': {
      en: 'After 12-18 months, the final result is visible. The transplanted beard hairs grow naturally and can be styled like your own beard.'
    },
  };

  const getTranslatedTitle = (title) => {
    if (language === 'de') return title;
    return processTranslations[title]?.en || title;
  };

  const getTranslatedDescription = (desc) => {
    if (language === 'de') return desc;
    return descriptionTranslations[desc]?.en || desc;
  };

  return (
    <section id={isBeard ? "behandlungsprozess-bart" : "behandlungsprozess"} className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0a0a0a]' : 'bg-white'
    }`}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className={`absolute top-0 right-1/4 w-[500px] h-[500px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
        }`} />
        <div className={`absolute bottom-1/4 left-0 w-[400px] h-[400px] rounded-full blur-[60px] ${
          isDark ? 'bg-[#5A8BA6]/5' : 'bg-[#7BA7C2]/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
          }`}>
            {sectionBadge}
          </span>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Der ' : 'The '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {sectionTitle}
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {sectionSubtitle}
          </p>
        </div>

        {/* Process Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {treatmentProcess.map((step, index) => (
            <div
              key={step.step}
              data-testid={`beard-process-${step.step}`}
              className={`relative group`}
            >
              {/* Step Number Badge */}
              <div className={`absolute -top-3 -left-3 z-10 w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                isDark 
                  ? 'bg-[#7BA7C2] text-white shadow-lg shadow-[#7BA7C2]/30'
                  : 'bg-[#5A8BA6] text-white shadow-lg shadow-[#5A8BA6]/30'
              }`}>
                {step.step}
              </div>

              {/* Card */}
              <div className={`rounded-2xl overflow-hidden border transition-all duration-500 h-full card-lift ${
                isDark 
                  ? 'bg-[#0f0f0f] border-white/5 hover:border-[#7BA7C2]/30'
                  : 'bg-white border-gray-200 hover:border-[#5A8BA6]/30'
              }`}>
                {/* Image */}
                <div className={`relative h-48 flex items-center justify-center p-6 ${
                  isDark ? 'bg-[#0a0a0a]' : 'bg-gray-100'
                }`}>
                  <img
                    src={step.image}
                    alt={step.title}
                    loading="lazy"
                    className={`w-full h-full object-contain max-h-36 transition-transform duration-500 group-hover:scale-110 ${
                      isDark ? 'filter brightness-90' : ''
                    }`}
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${
                    isDark 
                      ? 'text-white group-hover:text-[#7BA7C2]' 
                      : 'text-gray-900 group-hover:text-[#5A8BA6]'
                  }`}>
                    {getTranslatedTitle(step.title)}
                  </h3>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {getTranslatedDescription(step.description)}
                  </p>
                </div>
              </div>

              {/* Connecting Arrow removed as per user request */}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BeardProcess;
